import { ApplePayButtonTypes, applePayButtonTypes } from './types';


export const isAppropriateBrowserForApplePay = (): boolean => {
  const agent = navigator.userAgent;
  const isSafari = agent.indexOf('Safari') > -1;
  const isChrome = agent.indexOf('Chrome') > -1;
  const safariVersionMatch = agent.match(/Version\/([\d.]+)/);
  const isAppropriateVersion = safariVersionMatch
    ? parseInt(safariVersionMatch[1]) >= 17
    : false;

  return isSafari && !isChrome && isAppropriateVersion;
};

export const getCanMakeApplePayPayments = (): boolean => {
  // Check if Apple Pay is available
  const ApplePaySession = window.ApplePaySession;
  return ApplePaySession ?
    ApplePaySession.canMakePayments() && isAppropriateBrowserForApplePay() :
    false;
};

export const DEFAULT_APPLE_PAY_BUTTON_TYPE = 'plain';

export const isValidApplePayButtonType = (
  applePayButtonTypeArg: string | null | undefined,
): applePayButtonTypeArg is ApplePayButtonTypes => {
  if (!applePayButtonTypeArg) {
    return false;
  }

  return Object.values(applePayButtonTypes).includes(applePayButtonTypeArg as ApplePayButtonTypes);
};

export const BUSINESS_NAME_MAX_LENGTH = 50;

const isDisplayableChar = (char: string): boolean => {
  const code = char.charCodeAt(0);

  // Control characters
  if (code <= 0x1F || code === 0x7F) {
    return false;
  }

  // Zero-width characters
  if (code >= 0x200B && code <= 0x200F) {
    return false;
  }

  // Bidirectional formatting characters, they can be used to spoof the displayed value
  if (code >= 0x202A && code <= 0x202E) {
    return false;
  }

  // Byte order mark
  if (code === 0xFEFF) {
    return false;
  }

  return true;
};

/**
 * The business name comes from a query param, so it can't be trusted even though
 * the library validates it - strips the characters which are not displayable
 * on the Apple Pay popup and cuts the value to the maximum allowed length.
 */
export const sanitizeBusinessName = (businessName: string | null): string | null => {
  if (!businessName) {
    return null;
  }

  const sanitizedBusinessName = Array.from(businessName)
    .filter(isDisplayableChar)
    .join('')
    .trim();

  return sanitizedBusinessName
    ? sanitizedBusinessName.slice(0, BUSINESS_NAME_MAX_LENGTH)
    : null;
};

export const applePayBtnLimits = {
  borderRadius: () => ({
    defaultValue: 4,
    min: 0,
    max: 100,
  }),
  height: (buttonType: ApplePayButtonTypes) => {
    switch (buttonType) {
      case applePayButtonTypes.PLAIN:
        return {
          defaultValue: 40,
          min: 40,
          max: 100,
        };

      default:
        return {
          defaultValue: 40,
          min: 30,
          max: 100,
        };
    }
  },
  width: (buttonType: ApplePayButtonTypes) => {
    switch (buttonType) {
      case applePayButtonTypes.PLAIN:
        return {
          defaultValue: 240,
          min: 160,
          max: 800,
        };

      default:
        return {
          defaultValue: 240,
          min: 100,
          max: 800,
        };
    }
  },
};

export const clamp = (
  value: string | null,
  limits: {
    min: number,
    max: number,
    defaultValue: number,
  },
): number => {
  return (!value || isNaN(Number(value)))
    ? limits.defaultValue
    : Math.min(Math.max(Number(value), limits.min), limits.max);
};
